import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel, DataTable } from "@/components/admin/AdminPrimitives";
import { useI18n } from "@/lib/i18n";
import { useNewsletterAbonnes, useUnsubscribeNewsletter, useDeclencherDigestHebdomadaire } from "@/hooks/useNewsletter";
import type { AbonneDto } from "@/integrations/service/newsletter.service";

function Newsletter() {
  const { t } = useI18n();
  const [actif, setActif] = useState(true);
  const { data, isLoading } = useNewsletterAbonnes(actif);
  const unsubMutation = useUnsubscribeNewsletter();
  const declencherDigest = useDeclencherDigestHebdomadaire();

  function handleDeclencherDigest() {
    declencherDigest.mutate(undefined, {
      onSuccess: () =>
        toast.success(
          "Digest hebdomadaire déclenché — vérifiez la boîte mail des abonnés actifs (rien n'est envoyé s'il n'y a aucun article publié dans les 7 derniers jours)."
        ),
      onError: () => toast.error("Échec du déclenchement du digest"),
    });
  }

  // L'API retourne une Page Spring (data.content) ou un tableau direct
  const abonnes: AbonneDto[] = data?.content ?? (Array.isArray(data) ? data : []);
  const total: number = data?.totalElements ?? abonnes.length;

  function handleUnsubscribe(email: string) {
    unsubMutation.mutate(email, {
      onSuccess: () => toast.success(t("newsletter.unsubscribe.success")),
      onError: () => toast.error(t("admin.common.saveError")),
    });
  }

  return (
    <>
      <AdminTopbar
        title={t("admin.newsletter.title")}
        subtitle={t("admin.newsletter.subtitle")}
      />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="flex items-center justify-between rounded-md border border-border bg-card p-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Digest hebdomadaire</p>
            <p className="text-xs text-muted-foreground">
              Envoyé automatiquement chaque lundi à 7h. Déclenchez-le manuellement pour le tester.
            </p>
          </div>
          {/* <button
            onClick={handleDeclencherDigest}
            disabled={declencherDigest.isPending}
            className="flex items-center gap-2 rounded-md bg-foreground px-3 py-2 text-xs font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors"
          >
            {declencherDigest.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Tester le digest maintenant
          </button> */}
        </div>
        <ModulePanel
          title={t("admin.newsletter.subscribers")}
          description={t("admin.newsletter.total", { count: total })}
          action={
            <div className="flex gap-2">
              <button
                onClick={() => setActif(true)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors ${
                  actif
                    ? "bg-foreground text-background border-foreground"
                    : "border-border hover:bg-surface"
                }`}
              >
                {t("admin.newsletter.filter.active")}
              </button>
              <button
                onClick={() => setActif(false)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors ${
                  !actif
                    ? "bg-foreground text-background border-foreground"
                    : "border-border hover:bg-surface"
                }`}
              >
                {t("admin.newsletter.filter.inactive")}
              </button>
            </div>
          }
        >
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t("admin.common.loading")}</p>
          ) : abonnes.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("admin.newsletter.empty")}</p>
          ) : (
            <DataTable
              columns={[
                t("admin.newsletter.col.email"),
                t("admin.newsletter.col.canal"),
                t("admin.newsletter.col.langue"),
                t("admin.newsletter.col.date"),
                t("admin.common.col.actions"),
              ]}
              rows={abonnes.map((a) => [
                a.email,
                a.canal,
                a.langue ?? "—",
                a.inscritLe
                  ? new Date(a.inscritLe).toLocaleDateString()
                  : "—",
                actif ? (
                  <button
                    key={a.email}
                    onClick={() => handleUnsubscribe(a.email)}
                    disabled={unsubMutation.isPending}
                    className="rounded px-2 py-1 text-[10px] font-semibold bg-alert/10 text-alert hover:bg-alert/20 disabled:opacity-50"
                  >
                    {t("admin.newsletter.unsubscribe")}
                  </button>
                ) : (
                  "—"
                ),
              ])}
            />
          )}
        </ModulePanel>
      </div>
    </>
  );
}

export default Newsletter;
